import React, { useState } from "react";
import { axiosPostApi } from "../../helpers/interceptorServices";
import { apiList } from "../../helpers/apiList";
import CustomAlert from "../../helpers/CustomAlert";
import { useSelector, useDispatch } from "react-redux";
import { setBalance } from "../store/balance/balanceSlice";
import { formatDateToYYYYMMDD } from "../../helpers/DateHelpers";
import RcsBulkMessageModal from "../RcsBulkMessage/RcsBulkMessageModal";
import { CircularProgress } from "@mui/material";

const RCSTextWithCtaMsg = ({ fetchData }) => {
  const [formData, setFormData] = useState({
    campaignID: "",
    contacts: "",
    messageText: "",
    ctaButtonText: "",
    callbackData: "",
    ctaLinkUrl: "",
  });
  const [errors, setErrors] = useState({});
  const [isHovered, setIsHovered] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [checking, setChecking] = useState(false);
  const [capabilityResult, setCapabilityResult] = useState("");
  const [isVerified, setIsVerified] = useState(false);
  const [verifiedPhones, setVerifiedPhones] = useState([]);
  const [isBulkUpload, setIsBulkUpload] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [bulkFileName, setBulkFileName] = useState(null);
  const [csvFile, setCsvFile] = useState(null);

  const [bulkType, setBulkType] = useState("");
  const [isNameRequired, setIsNameRequired] = useState(false);

  const loggedInUserName = sessionStorage.getItem("username");
  const { rcsAgentId } = useSelector((state) => state.auth);
  const dispatch = useDispatch();

  const formatContacts = (input) => {
    const rawNumbers = input.split(",").map((num) => num.trim());
    const validNumbers = rawNumbers.filter((num) => /^[6-9]\d{9}$/.test(num));
    return validNumbers.map((num) => `+91${num}`);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "contacts") {
      const cleanedValue = value.replace(/[^\d,]/g, "");
      const numbers = cleanedValue.split(",").map((num) => num.trim());
      const filteredNumbers = numbers.map((num) => num.slice(0, 10));
      const finalValue = filteredNumbers.join(",");
      setFormData((prev) => ({ ...prev, contacts: finalValue }));
      setCapabilityResult("");
      const validNumbers = filteredNumbers.filter((num) =>
        /^[6-9]\d{9}$/.test(num)
      );
      const allVerified =
        validNumbers.length > 0 &&
        validNumbers.every((num) => verifiedPhones.includes(num));
      setIsVerified(allVerified);
    } else if (name === "campaignID") {
      if (formData.campaignID === "" && value.trim() !== "") {
        const now = new Date();
        const hours = now.getHours();
        if (hours < 9 || hours >= 21) {
          CustomAlert({
            title: "Info",
            text: "RCS services are available only between 9 AM to 9 PM. Please try later.",
            icon: "info",
            confirmButtonText: "Okay",
            allowOutsideClick: false,
          });
          return;
        }
      }
      setFormData((prev) => ({ ...prev, [name]: value }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const fieldErrors = validateForm(name, value);
    setErrors((prev) => ({ ...prev, ...fieldErrors }));
  };

  const validateForm = (name, value) => {
    const newErrors = {};
    const repetitiveNumberPattern = /^(\d)\1{9}$/;
    const campaignIDRegex = /^[a-zA-Z0-9 _-]{4,20}$/;
    const urlRegex = /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w-./?%&=]*)?$/;

    if (name === "campaignID" || !name) {
      if (!formData.campaignID.trim()) {
        newErrors.campaignID = "Campaign ID is required";
      } else if (!campaignIDRegex.test(formData.campaignID.trim())) {
        newErrors.campaignID =
          "Campaign ID must be 4–20 characters: only letters, numbers, spaces, _ or - allowed";
      }
    }

    if (!isBulkUpload && (name === "contacts" || !name)) {
      if (!formData.contacts.trim()) {
        newErrors.contacts = "Contacts are required";
      } else if (!/^[6-9]\d{9}(,[6-9]\d{9})*$/.test(formData.contacts)) {
        newErrors.contacts =
          "Please enter a valid 10 digit number starting with either 6, 7, 8 or 9";
      } else if (
        formData.contacts
          .split(",")
          .some((num) => repetitiveNumberPattern.test(num.trim()))
      ) {
        newErrors.contacts = "Repetitive phone numbers are not allowed";
      }
    }

    if (name === "messageText" || !name) {
      if (!formData.messageText.trim()) {
        newErrors.messageText = "Message Text is required";
      } else if (formData.messageText.length > 300) {
        newErrors.messageText = "Message Text must be 50 characters or less";
      }
    }

    if (name === "ctaButtonText" || !name) {
      if (!formData.ctaButtonText.trim()) {
        newErrors.ctaButtonText = "CTA Button Text is required";
      }
    }

    if (name === "callbackData" || !name) {
      if (!formData.callbackData.trim()) {
        newErrors.callbackData = "Callback Data is required";
      }
    }

    if (name === "ctaLinkUrl" || !name) {
      if (!formData.ctaLinkUrl.trim()) {
        newErrors.ctaLinkUrl = "CTA Link URL is required";
      } else if (!urlRegex.test(formData.ctaLinkUrl)) {
        newErrors.ctaLinkUrl = "Enter a valid URL (e.g., https://example.com)";
      }
    }

    if (name === "messageText" || !name) {
      if (!formData.messageText.trim()) {
        newErrors.messageText = "Message Text is required";
      } else if (formData.messageText.length > 300) {
        newErrors.messageText = "Message Text must be 300 characters or less";
      } else if (
        bulkType === "name" &&
        !/\{\{Name\}\}/.test(formData.messageText)
      ) {
        newErrors.messageText =
          "Message Text must include {{Name}} placeholder for Bulk Name upload";
      }
    }

    return newErrors;
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const checkCapability = async () => {
    if (isBulkUpload) return;

    const contacts = formatContacts(formData.contacts);
    if (contacts.length === 0) {
      setCapabilityResult("⚠️ Please enter valid phone numbers");
      return false;
    }
    setChecking(true);
    try {
      const headers = {
        rcskey: rcsAgentId,
        "Content-Type": "application/json",
      };
      const payload = {
        PhoneNumbers: contacts,
        username: loggedInUserName,
      };
      const response = await axiosPostApi(
        apiList.CHECK_CAPABILITY,
        payload,
        headers
      );
      if (
        response?.data?.meta?.status === "00" &&
        response?.data?.data?.length
      ) {
        setIsVerified(true);
        const newVerifiedNumbers = formData.contacts
          .split(",")
          .map((num) => num.trim())
          .filter((num) => /^[6-9]\d{9}$/.test(num));
        setVerifiedPhones((prev) => [
          ...new Set([...prev, ...newVerifiedNumbers]),
        ]);
        CustomAlert({
          title: "Success",
          text: response?.data?.meta?.message,
          icon: "success",
          confirmButtonText: "Okay",
          allowOutsideClick: false,
        });
      } else {
        setIsVerified(false);
        CustomAlert({
          title: "Error",
          text: response?.data?.meta?.message || "Capability check failed",
          icon: "error",
          confirmButtonText: "Okay",
          allowOutsideClick: false,
        });
      }
    } catch (err) {
      console.error("checkCapability error:", err);
      setCapabilityResult(`⚠️ Network Error: ${err.message}`);
      setIsVerified(false);
    } finally {
      setChecking(false);
    }
  };

  const fetchDashboardData = () => {
    const endDate = formatDateToYYYYMMDD(new Date());
    const startDate1 = new Date(new Date().setDate(new Date().getDate() - 90));
    const finalStartDate = formatDateToYYYYMMDD(startDate1);
    const requestBody = {
      startDate: finalStartDate,
      endDate: endDate,
      username: loggedInUserName,
    };
    axiosPostApi(apiList.DASHBOARD_DATA, requestBody)
      .then((response) => {
        if (response?.status === 200 && response?.data?.respCode === "00") {
          dispatch(setBalance(response?.data?.data?.balanceDetails));
          fetchData(1);
        }
      })
      .catch((error) => {
        console.error("Error fetching SMS overview data", error);
      });
  };

  const handleCsvBulkUpload = async () => {
    if (!csvFile) {
      setErrors((prev) => ({ ...prev, csv: "Please select a CSV file." }));
      return;
    }
    const formDataA = new FormData();
    formDataA.append("username", loggedInUserName);
    formDataA.append("rcsType", "PLAINTEXT_CTA");
    formDataA.append(
      "rcsMessage",
      JSON.stringify({
        username: loggedInUserName,
        campaignID: formData.campaignID,
        contacts: [""],
        data: {
          content: {
            plainText: formData.messageText,
            suggestions: [
              {
                action: {
                  plainText: formData.ctaButtonText,
                  postBack: {
                    data: formData.callbackData,
                  },
                  openUrl: {
                    url: formData.ctaLinkUrl,
                  },
                },
              },
            ],
          },
        },
      })
    );
    formDataA.append("file", csvFile);
    // formDataA.append("isNameRequired", false);
    formDataA.append("isNameRequired", isNameRequired);
    try {
      const res = await axiosPostApi(
        apiList.RCS_UPLOAD_MOBILE_EXCEL,
        formDataA,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      if (res?.status === 200 && res?.data?.respCode === "00") {
        CustomAlert({
          title: "Success",
          text: res.data.message,
          icon: "success",
          confirmButtonText: "Okay",
        }).then(() => {
          fetchDashboardData();
          setErrors({});
          setIsBulkUpload(false);
          setBulkFileName(null);
          setCsvFile(null);
        });
        resetForm();
      } else if (res?.data?.respCode === "05") {
        CustomAlert({
          title: "Info",
          text: res?.data?.message || "Invalid credentials. Please try again!",
          icon: "info",
          confirmButtonText: "Retry",
        });
      } else {
        CustomAlert({
          title: "Error",
          text: res?.data?.message || "Bulk upload failed",
          icon: "error",
          confirmButtonText: "Retry",
        });
      }
    } catch (err) {
      CustomAlert({
        title: "Error",
        text: `Upload error: ${err.message}`,
        icon: "error",
        confirmButtonText: "Retry",
      });
    }
  };

  const resetForm = () => {
    setFormData({
      campaignID: "",
      contacts: "",
      messageText: "",
      ctaButtonText: "",
      callbackData: "",
      ctaLinkUrl: "",
    });
    setIsVerified(false);
    setIsBulkUpload(false);
    setBulkFileName(null);
    setCsvFile(null);
    setCapabilityResult("");
    setErrors({});
    setBulkType("");
    setIsNameRequired(false);
  };

  const handleRemoveFile = () => {
    setCsvFile(null);
    setBulkFileName(null);
    setIsBulkUpload(false);
    setErrors((prev) => ({ ...prev, csv: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const now = new Date();
    const hours = now.getHours();
    if (hours < 9 || hours >= 21) {
      CustomAlert({
        title: "Info",
        text: "RCS services are available only between 9 AM to 9 PM. Please try later.",
        icon: "info",
        confirmButtonText: "Okay",
        allowOutsideClick: false,
      });
      return;
    }

    if (!isVerified && !isBulkUpload) {
      CustomAlert({
        title: "Info",
        text: "Please select either Check, Bulk or Bulk Name before proceeding.",
        icon: "info",
        confirmButtonText: "Okay",
        allowOutsideClick: false,
      });
      return;
    }
    const errs = validateForm();
    if (Object.keys(errs).length) {
      setErrors(errs);
      CustomAlert({ title: "Error", text: "Fix form errors.", icon: "error" });
      return;
    }
    if (isBulkUpload) {
      await handleCsvBulkUpload();
      return;
    }
    try {
      setIsSubmitting(true);
      const payload = {
        campaignID: formData.campaignID,
        contacts: formatContacts(formData.contacts),
        data: {
          content: {
            plainText: formData.messageText,
            suggestions: [
              {
                action: {
                  plainText: formData.ctaButtonText,
                  postBack: {
                    data: formData.callbackData,
                  },
                  openUrl: {
                    url: formData.ctaLinkUrl,
                  },
                },
              },
            ],
          },
        },
        username: loggedInUserName,
      };
      const headers = { rcskey: rcsAgentId };
      const response = await axiosPostApi(
        apiList.PLAIN_TEXT_CTA,
        payload,
        headers
      );
      const status = response?.data?.meta?.status;
      const message = response?.data?.meta?.message || "No message";
      if (status === "00") {
        CustomAlert({
          title: "Success",
          text: message,
          icon: "success",
          confirmButtonText: "Okay",
        }).then(() => {
          fetchDashboardData();
          setErrors({});
        });
        resetForm();
      } else {
        CustomAlert({
          title: "Error",
          text: message,
          icon: "error",
          confirmButtonText: "Retry",
        });
      }
    } catch (error) {
      CustomAlert({
        title: "Error",
        text: "An error occurred while sending the message.",
        icon: "error",
        confirmButtonText: "Retry",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const disableBtn = isBulkUpload || isVerified;

  return (
    <div
      style={{
        padding: "20px",
        fontFamily: "sans-serif",
        backgroundColor: "#ffffff",
        boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
        borderRadius: "12px",
        maxWidth: "900px",
        margin: "40px 0 40px 20px",
      }}
    >
      <h2 style={{ color: "#00b6CE", marginBottom: "20px", textAlign: "left" }}>
        Send RCS Text With CTA Message
      </h2>
      <form
        onSubmit={handleSubmit}
        style={{ display: "flex", flexDirection: "column", gap: "20px" }}
      >
        <div
          style={{
            display: "flex",
            gap: "20px",
            flexWrap: "wrap",
            width: "68%",
          }}
        >
          <div style={{ flex: "1", minWidth: "200px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "5px",
                fontWeight: "bold",
                fontSize: "16px",
                textAlign: "left",
              }}
            >
              Campaign ID
            </label>
            <input
              type="text"
              name="campaignID"
              value={formData.campaignID}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Enter campaign ID"
              style={{
                padding: "8px 12px",
                borderRadius: "12px",
                border: errors.campaignID ? "1px solid red" : "1px solid #ccc",
                backgroundColor: "#f1f1f1",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                fontSize: "16px",
                width: "100%",
                height: "40px",
                outline: "none",
                transition: "all 0.3s ease",
              }}
            />
            {errors.campaignID && (
              <span style={{ color: "red", fontSize: "14px" }}>
                {errors.campaignID}
              </span>
            )}
          </div>
          <div style={{ flex: "1", minWidth: "200px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "5px",
                fontWeight: "bold",
                fontSize: "16px",
                textAlign: "left",
              }}
            >
              Contacts
            </label>
            <div style={{ display: "flex", gap: "8px" }}>
              <input
                type="text"
                name="contacts"
                value={formData.contacts}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={isBulkUpload}
                placeholder="Enter phone number"
                style={{
                  flex: "1",
                  padding: "8px 12px",
                  borderRadius: "12px",
                  border:
                    errors.contacts && !isBulkUpload
                      ? "1px solid red"
                      : "1px solid #ccc",
                  backgroundColor: "#f1f1f1",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  fontSize: "16px",
                  height: "40px",
                  outline: "none",
                }}
              />
              <button
                type="button"
                onClick={checkCapability}
                disabled={
                  checking ||
                  !rcsAgentId ||
                  (formData.contacts &&
                    formData.contacts
                      .split(",")
                      .map((num) => num.trim())
                      .filter((num) => /^[6-9]\d{9}$/.test(num))
                      .every((num) => verifiedPhones.includes(num)))
                }
                title={
                  formData.contacts &&
                  formData.contacts
                    .split(",")
                    .map((num) => num.trim())
                    .filter((num) => /^[6-9]\d{9}$/.test(num))
                    .every((num) => verifiedPhones.includes(num))
                    ? "All numbers already verified"
                    : "Check phone numbers"
                }
                style={{
                  padding: "8px 12px",
                  borderRadius: "12px",
                  border: "none",
                  backgroundColor: "#00BCD4",
                  color: "white",
                  fontSize: "16px",
                  fontWeight: "bold",
                  height: "40px",
                  cursor:
                    checking ||
                    !rcsAgentId ||
                    disableBtn ||
                    (formData.contacts &&
                      formData.contacts
                        .split(",")
                        .map((num) => num.trim())
                        .filter((num) => /^[6-9]\d{9}$/.test(num))
                        .every((num) => verifiedPhones.includes(num)))
                      ? "not-allowed"
                      : "pointer",
                  opacity:
                    checking ||
                    !rcsAgentId ||
                    disableBtn ||
                    (formData.contacts &&
                      formData.contacts
                        .split(",")
                        .map((num) => num.trim())
                        .filter((num) => /^[6-9]\d{9}$/.test(num))
                        .every((num) => verifiedPhones.includes(num)))
                      ? 0.6
                      : 1,
                }}
              >
                {checking ? (
                  <CircularProgress style={{ width: "20px", height: "20px" }} />
                ) : (
                  "Check"
                )}
              </button>
              <span>OR</span>
              <button
                type="button"
                onClick={() => {
                  setShowModal(true);
                  setBulkType("bulk");
                  setIsNameRequired(false);
                  setFormData((prev) => ({ ...prev, contacts: "" }));
                }}
                disabled={disableBtn}
                style={{
                  padding: "8px 12px",
                  borderRadius: "10px",
                  border: "none",
                  backgroundColor: "#00BCD4",
                  color: "white",
                  fontSize: "16px",
                  fontWeight: "bold",
                  width: "90px",
                  height: "40px",
                  cursor: disableBtn ? "not-allowed" : "pointer",
                  opacity: disableBtn ? 0.6 : 1,
                }}
              >
                Bulk
              </button>

              <span>OR</span>
              <button
                type="button"
                onClick={() => {
                  setShowModal(true);
                  setBulkType("name");
                  setIsNameRequired(true);
                  setFormData((prev) => ({ ...prev, contacts: "" }));
                }}
                disabled={disableBtn}
                style={{
                  padding: "8px 12px",
                  borderRadius: "10px",
                  border: "none",
                  backgroundColor: "#00BCD4",
                  color: "white",
                  fontSize: "16px",
                  fontWeight: "bold",
                  minWidth: "120px",
                  height: "40px",
                  cursor: disableBtn ? "not-allowed" : "pointer",
                  opacity: disableBtn ? 0.6 : 1,
                }}
              >
                Bulk Name
              </button>

              {/* <button
                onClick={() => {
                  setShowModal(true);
                  setFormData((prev) => ({ ...prev, contacts: "" }));
                }}
                type="button"
                style={{
                  padding: "8px 12px",
                  borderRadius: "10px",
                  border: "none",
                  backgroundColor: "#00BCD4",
                  color: "white",
                  fontSize: "16px",
                  fontWeight: "bold",
                  width: "90px",
                  height: "40px",
                  cursor:
                    checking ||
                    !rcsAgentId ||
                    disableBtn ||
                    verifiedPhones.includes(formData.contacts)
                      ? "not-allowed"
                      : "pointer",
                  opacity:
                    checking ||
                    !rcsAgentId ||
                    disableBtn ||
                    verifiedPhones.includes(formData.contacts)
                      ? 0.6
                      : 1,
                  filter:
                    verifiedPhones.includes(formData.contacts) || disableBtn
                      ? "blur(0.6px)"
                      : "none",
                }}
                disabled={disableBtn}
              >
                Bulk
              </button> */}
            </div>
            {isVerified &&
            formData.contacts
              .split(",")
              .map((num) => num.trim())
              .filter((num) => /^[6-9]\d{9}$/.test(num))
              .every((num) => verifiedPhones.includes(num)) ? (
              <span
                style={{ marginTop: "6px", color: "green", fontSize: "14px" }}
              >
                verified
              </span>
            ) : (
              capabilityResult && (
                <div
                  style={{ marginTop: "6px", color: "#333", fontSize: "14px" }}
                >
                  {capabilityResult}
                </div>
              )
            )}
            {!isBulkUpload && errors.contacts && (
              <span style={{ color: "red", fontSize: "14px" }}>
                {errors.contacts}
              </span>
            )}
            {isBulkUpload && bulkFileName && (
              <div
                style={{
                  marginTop: "10px",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <div
                  style={{
                    display: "inline-block",
                    padding: "6px 16px",
                    backgroundColor: "#e0f7fa",
                    borderRadius: "999px",
                    fontSize: "14px",
                    color: "#007c91",
                    fontWeight: 600,
                    fontFamily: "Segoe UI, sans-serif",
                    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.06)",
                    letterSpacing: "0.3px",
                  }}
                >
                  {bulkFileName.name}
                </div>
                {/* New Remove File Button */}
                <button
                  type="button"
                  onClick={handleRemoveFile}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "#007c91",
                    fontSize: "18px",
                    cursor: "pointer",
                  }}
                  title="Remove file"
                >
                  ❌
                </button>
              </div>
            )}
          </div>
        </div>
        <div
          style={{
            display: "flex",
            gap: "20px",
            flexWrap: "wrap",
            width: "100%",
          }}
        >
          <div style={{ flex: "1", minWidth: "200px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "5px",
                fontWeight: "bold",
                fontSize: "16px",
                textAlign: "left",
              }}
            >
              CTA Button Text
            </label>
            <input
              type="text"
              name="ctaButtonText"
              value={formData.ctaButtonText}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Enter CTA Button Text"
              style={{
                padding: "8px 12px",
                borderRadius: "12px",
                border: errors.ctaButtonText
                  ? "1px solid red"
                  : "1px solid #ccc",
                backgroundColor: "#f1f1f1",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                fontSize: "16px",
                width: "100%",
                height: "40px",
                outline: "none",
                transition: "all 0.3s ease",
              }}
            />
            {errors.ctaButtonText && (
              <span style={{ color: "red", fontSize: "14px" }}>
                {errors.ctaButtonText}
              </span>
            )}
          </div>
          <div style={{ flex: "1", minWidth: "200px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "5px",
                fontWeight: "bold",
                fontSize: "16px",
                textAlign: "left",
              }}
            >
              Callback Data
            </label>
            <input
              type="text"
              name="callbackData"
              value={formData.callbackData}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Enter Callback Data"
              style={{
                padding: "8px 12px",
                borderRadius: "12px",
                border: errors.callbackData
                  ? "1px solid red"
                  : "1px solid #ccc",
                backgroundColor: "#f1f1f1",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                fontSize: "16px",
                width: "100%",
                height: "40px",
                outline: "none",
                transition: "all 0.3s ease",
              }}
            />
            {errors.callbackData && (
              <span style={{ color: "red", fontSize: "14px" }}>
                {errors.callbackData}
              </span>
            )}
          </div>
        </div>
        <div style={{ width: "100%" }}>
          <label
            style={{
              display: "block",
              marginBottom: "5px",
              fontWeight: "bold",
              fontSize: "16px",
              textAlign: "left",
            }}
          >
            CTA Link URL
          </label>
          <input
            type="text"
            name="ctaLinkUrl"
            value={formData.ctaLinkUrl}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="https://example.com"
            style={{
              padding: "8px 12px",
              borderRadius: "12px",
              border: errors.ctaLinkUrl ? "1px solid red" : "1px solid #ccc",
              backgroundColor: "#f1f1f1",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              fontSize: "16px",
              width: "100%",
              height: "40px",
              outline: "none",
              transition: "all 0.3s ease",
            }}
          />
          {errors.ctaLinkUrl && (
            <span style={{ color: "red", fontSize: "14px" }}>
              {errors.ctaLinkUrl}
            </span>
          )}
        </div>
        <div style={{ width: "100%" }}>
          <label
            style={{
              display: "block",
              marginBottom: "5px",
              fontWeight: "bold",
              fontSize: "16px",
              textAlign: "left",
            }}
          >
            Plain Text Message
          </label>
          <textarea
            name="messageText"
            value={formData.messageText}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Enter message text (max 50 characters)"
            maxLength={300}
            style={{
              padding: "8px 12px",
              borderRadius: "12px",
              border: errors.messageText ? "1px solid red" : "1px solid #ccc",
              backgroundColor: "#f1f1f1",
              fontSize: "16px",
              width: "100%",
              minHeight: "100px",
              resize: "vertical",
              outline: "none",
            }}
          />
          {errors.messageText && (
            <span style={{ color: "red", fontSize: "14px" }}>
              {errors.messageText}
            </span>
          )}
        </div>
        <div style={{ display: "flex", justifyContent: "center" }}>
          <button
            type="submit"
            style={{
              backgroundColor: isHovered || isActive ? "#ffffff" : "#00BCD4",
              color: isHovered || isActive ? "#00B6CE" : "#ffffff",
              border: isHovered || isActive ? "1px solid #00B6CE" : "none",
              padding: "12px 36px",
              fontSize: "16px",
              fontWeight: "600",
              borderRadius: "999px",
              cursor: isSubmitting ? "not-allowed" : "pointer",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
              transition:
                "transform 0.1s ease-in-out, background-color 0.3s, color 0.3s, border 0.3s",
              alignSelf: "center",
              marginTop: "7px",
              transform: isActive ? "scale(0.95)" : "scale(1)",
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onMouseDown={() => setIsActive(true)}
            onMouseUp={() => setIsActive(false)}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <CircularProgress style={{ width: "20px", height: "20px" }} />
            ) : (
              "Send"
            )}
          </button>
        </div>
      </form>
      <RcsBulkMessageModal
        show={showModal}
        handleClose={handleCloseModal}
        activeTab="PLAINTEXT_CTA"
        setIsBulkUpload={setIsBulkUpload}
        setBulkFileName={setBulkFileName}
        setCsvFile={setCsvFile}
        csvFile={csvFile}
        bulkType={bulkType}
      />
    </div>
  );
};

export default RCSTextWithCtaMsg;
